import { formatNumber } from '../../lib/formatters.js';
import { FIELD_LABELS, REQUIRED_FIELDS } from './constants.js';

interface FormFieldSummary {
  fieldName: '_summary';
  totalUsers: number;
  interactedUsers: number;
  noInteractionUsers: number;
  noInteractionRate: number;
}

interface FormFieldRow {
  fieldName: string;
  filledUsers: number;
  totalUsers: number;
  fillRate: number;
}

/**
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-business 新規登録ファネルの「基本情報フィールド入力率」深掘り。サマリー行（ページ到達 / 操作あり / 未操作離脱）+ フィールドごとの入力済み数 / 入力率（必須フィールドは ★）を表示する。
 * @graph-connects formatNumber [calls] 数値フォーマット
 */
export function BasicInfoFieldAnalysis({ data }: { data: Record<string, unknown>[] }) {
  const summaryRaw = data.find((d) => d.fieldName === '_summary');
  const summary = summaryRaw as unknown as FormFieldSummary | undefined;
  const rows = data.filter((d) => d.fieldName !== '_summary') as unknown as FormFieldRow[];

  if (rows.length === 0) {
    return (
      <div className="card">
        <p className="text-sm text-[var(--color-text-muted)] text-center py-8">
          データがありません
        </p>
      </div>
    );
  }

  // フィールド定義順にソート
  const fieldOrder = Object.keys(FIELD_LABELS);
  const sorted = [...rows].sort(
    (a, b) => fieldOrder.indexOf(a.fieldName) - fieldOrder.indexOf(b.fieldName),
  );

  const avgFillRate =
    sorted.length > 0
      ? Math.round((sorted.reduce((s, r) => s + r.fillRate, 0) / sorted.length) * 10) / 10
      : 0;

  return (
    <div className="card animate-fade-in-up">
      {summary && (
        <div className="flex gap-4 mb-4">
          <div className="flex-1 rounded-lg bg-[var(--color-bg-hover)] p-3 text-center">
            <div className="text-xs text-[var(--color-text-muted)] mb-1">ページ到達</div>
            <div className="text-lg font-semibold font-mono text-[var(--color-text-primary)]">
              {formatNumber(summary.totalUsers)}
            </div>
          </div>
          <div className="flex-1 rounded-lg bg-[var(--color-bg-hover)] p-3 text-center">
            <div className="text-xs text-[var(--color-text-muted)] mb-1">フォーム操作あり</div>
            <div className="text-lg font-semibold font-mono text-[var(--color-text-primary)]">
              {formatNumber(summary.interactedUsers)}
            </div>
          </div>
          <div className="flex-1 rounded-lg bg-[var(--color-bg-hover)] p-3 text-center">
            <div className="text-xs text-[var(--color-text-muted)] mb-1">未操作離脱</div>
            <div className="text-lg font-semibold font-mono text-[var(--color-error)]">
              {formatNumber(summary.noInteractionUsers)}
              <span className="text-xs ml-1">({String(summary.noInteractionRate)}%)</span>
            </div>
          </div>
        </div>
      )}
      <p className="text-xs text-[var(--color-text-muted)] mb-3">
        1度でも入力したユーザー数 / フォーム到達ユーザー数（★ = 必須フィールド）
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--color-border)]">
              <th className="text-left py-2 px-3 text-xs font-medium text-[var(--color-text-muted)]">
                フィールド
              </th>
              <th className="text-right py-2 px-3 text-xs font-medium text-[var(--color-text-muted)]">
                入力済み
              </th>
              <th className="text-right py-2 px-3 text-xs font-medium text-[var(--color-text-muted)]">
                母数
              </th>
              <th className="text-right py-2 px-3 text-xs font-medium text-[var(--color-text-muted)] w-48">
                入力率
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((row) => {
              const isRequired = REQUIRED_FIELDS.has(row.fieldName);
              const rateColor =
                row.fillRate >= 90
                  ? 'text-[var(--color-success)]'
                  : row.fillRate >= 50
                    ? 'text-[var(--color-warning)]'
                    : 'text-[var(--color-error)]';
              const barColor =
                row.fillRate >= 90
                  ? 'var(--color-success)'
                  : row.fillRate >= 50
                    ? 'var(--color-warning)'
                    : 'var(--color-error)';

              return (
                <tr
                  key={row.fieldName}
                  className="border-b border-[var(--color-border)] last:border-b-0 hover:bg-[var(--color-bg-hover)]"
                >
                  <td className="py-2 px-3 text-[var(--color-text-primary)]">
                    {FIELD_LABELS[row.fieldName] ?? row.fieldName}
                    {isRequired && (
                      <span className="ml-1 text-[var(--color-accent-primary)] text-xs">★</span>
                    )}
                  </td>
                  <td className="py-2 px-3 text-right font-mono text-[var(--color-text-primary)]">
                    {formatNumber(row.filledUsers)}
                  </td>
                  <td className="py-2 px-3 text-right font-mono text-[var(--color-text-muted)]">
                    {formatNumber(row.totalUsers)}
                  </td>
                  <td className="py-2 px-3">
                    <div className="flex items-center justify-end gap-2">
                      <div className="w-24 h-1.5 rounded-full bg-[var(--color-bg-hover)] overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{
                            width: `${String(Math.min(row.fillRate, 100))}%`,
                            background: barColor,
                          }}
                        />
                      </div>
                      <span className={`font-mono text-xs w-12 text-right ${rateColor}`}>
                        {String(row.fillRate)}%
                      </span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-[var(--color-border)]">
              <td className="py-2 px-3 text-xs font-semibold text-[var(--color-text-secondary)]">
                平均
              </td>
              <td />
              <td />
              <td className="py-2 px-3 text-right">
                <span className="font-mono text-xs font-semibold text-[var(--color-text-primary)]">
                  {String(avgFillRate)}%
                </span>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
